import React from "react";
import { FaAward, FaUsers, FaBriefcase, FaGraduationCap, FaGlobe, FaUser, FaBuilding } from "react-icons/fa";
import {
  Newspaper,
  Lightbulb,
  FileText,
  MonitorPlay,
  Mail,
} from "lucide-react";

const newsSections = [
  {
    title: "Latest Announcements",
    description:
      "Stay updated with official Indo European Business Council announcements, including delegation updates, event outcomes, awards nominations, and bilateral trade developments.",
    icon: <Newspaper size={40} className="text-indigo-600" />,
  },
  {
    title: "Spotlight Stories",
    description:
      "Exclusive interviews, member spotlights, and behind-the-scenes features that illuminate the human side of cross-continental business.",
    icon: <Lightbulb size={40} className="text-green-500" />,
  },
  {
    title: "Policy & Market Briefs",
    description:
      "Policy briefs and market insights decode regulations, bilateral agreements, EU funding programmes, FDI frameworks, and emerging industry trends.",
    icon: <FileText size={40} className="text-yellow-500" />,
  },
  {
    title: "Media Mentions & Coverage",
    description:
      "Browse news articles, television interviews, and digital features that highlight Indo European Business Council initiatives and impact.",
    icon: <MonitorPlay size={40} className="text-purple-500" />,
  },
];

const membershipLevels = [
  {
    icon: <FaAward className="text-4xl text-yellow-500 mb-5" />,
    title: "Gold Corporate Membership",
    subtitle: "Ideal for established businesses looking to deepen their international connections",
    details: [
      <span key="elig">Eligibility: <strong>Up to 4 nominees from the member organization</strong>.</span>
    ],
    benefits: [
      "VIP attendee badges at all networking events organized by the chamber.",
      "Complimentary VIP invites to all cultural and international events.",
      "25% discount on booth fees at trade events globally.",
      "Access to secretarial and mail management services in Mumbai, India, and Budapest.",
      "Free double-page profile in the chamber’s business activities directory.",
      "Three premium banner advertisements per month on the chamber’s website."
    ],
  },
  {
    icon: <FaBuilding className="text-4xl text-green-600 mb-5" />,
    title: "Corporate Membership",
    subtitle: "Designed for companies seeking to enhance their business outreach and gain strategic insights",
    details: [],
    benefits: [
      "Membership for 3 nominees.",
      "VIP invites to all chamber networking events.",
      "Full-page profile in the member directory.",
      "Use of a corporate mailing address in Budapest.",
      "Mail forwarding and visitor reception services.",
      "10% discount on booth fees at all European trade events.",
      "One free standard banner advertisement per month on the chamber’s website."
    ],
  },
  {
    icon: <FaBriefcase className="text-4xl text-blue-500 mb-5" />,
    title: "Small Corporate/Startup Membership",
    subtitle: "Tailored for startups and small enterprises aiming to expand their market presence and network",
    details: [],
    benefits: [
      "Free invitations to select networking events, with discounted rates for others.",
      "Listing in the members’ roster.",
      "Discounted invites to select international events.",
      "Free subscription to the chamber’s newsletter."
    ],
  },
  {
    icon: <FaUser className="text-4xl text-indigo-600 mb-5" />,
    title: "Individual Membership",
    subtitle: "Perfect for professionals looking to network and engage with industry leaders",
    details: [],
    benefits: [
      "Invitation to the chamber’s networking events.",
      "Listing in the members’ roster.",
      "Invitations to select European events.",
      "Free subscription to the chamber’s newsletter."
    ],
  },
  {
    icon: <FaGraduationCap className="text-4xl text-purple-500 mb-5" />,
    title: "Young Champs Membership (Students)",
    subtitle: "Geared towards students interested in gaining international exposure and experience",
    details: [],
    benefits: [
      "Free invitations to select networking events, with discounted rates for others.",
      "Listing in the members’ roster.",
      "Opportunities for volunteer experience and internships, with certification."
    ],
  },
  {
    icon: <FaGlobe className="text-4xl text-gray-700 mb-5" />,
    title: "Overseas Membership",
    subtitle: "For international members who wish to establish a presence and expand their operations within the European Union",
    details: [
      "Associate Member (Individual/Corporate)",
      "Executive Member (Corporate)"
    ],
    benefits: [],
  },
];

const IEBCHighlights = () => {
  return (
    <div className="bg-gray-50">
      {/* Page Header */}
      <header className="text-center py-20 px-6 md:px-20">
        <h1 className="text-5xl md:text-6xl font-Agatho_Regular font-extrabold text-gray-900 mb-4">
          Indo European Business Council Highlights
        </h1>
        <p className="text-xl text-gray-700 max-w-3xl mx-auto">
          Showcasing Our Signature Events, Latest News, and Insights Driving Indo European Business Collaboration
        </p>
      </header>

      {/* Membership Section */}
      <section className="pb-20 px-4 md:px-14">
        <h2 className="text-3xl md:text-4xl font-extrabold font-Agatho_Regular text-gray-900 mb-10 text-center">
          Membership Opportunities at the <span className="font-extrabold">Indo European Business Council</span>
        </h2>
   
        <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-6 text-center">
          Join the “Indo-European Business Council” and become part of an influential network that fosters economic and cultural ties between India and Europe. Our membership packages are tailored to meet the needs of businesses and individuals across various sectors and stages of growth. Explore our diverse membership options and select the one that best fits your needs and aspirations.
        </p>
        <div className="grid gap-8 md:gap-9 md:grid-cols-2 lg:grid-cols-3">
          {membershipLevels.map((level, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg p-6 md:p-8 flex flex-col transition-transform hover:scale-105 duration-300"
            >
              <div className="mb-4 md:mb-5">{level.icon}</div>
              <h3 className="text-2xl md:text-3xl  font-serif text-gray-900 mb-4">{level.title}</h3>
              <p className="text-xs md:text-sm text-gray-700 mb-1 md:mb-2 italic">{level.subtitle}</p>
              {level.details && level.details.length > 0 && (
                <ul className="list-disc list-inside text-gray-600 text-xs md:text-sm mb-1 md:mb-2">
                  {level.details.map((d, k) => <li key={k}>{d}</li>)}
                </ul>
              )}
              {level.benefits && level.benefits.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-0.5 mt-2 text-sm">Benefits:</h4>
                  <ul className="list-disc list-inside text-gray-600 text-xs md:text-sm">
                    {level.benefits.map((b, bidx) => <li key={bidx}>{b}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Events Section */}
      <section className="pb-16 px-6 md:px-20">
        <h2 className="text-4xl md:text-5xl font-bold font-Agatho_Regular text-gray-900 mb-8 text-center">
          Events at Indo European Business Council
        </h2>
        <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-8 text-center">
          Showcasing Business Excellence and Cultural Integration
        </p>
        <div className="max-w-4xl mx-auto mb-8 text-center">
          <p className="text-gray-700 mb-4">
            At the “Indo-European Business Council”, we are committed to fostering a robust platform for cultural and business exchanges between India and Europe. Our events are carefully designed to promote international trade, facilitate business networking, and celebrate cultural diversity. Explore our lineup of key events and discover opportunities to engage with industry leaders and innovators.
          </p>
        </div>
        <div className="text-center mb-8">
          <h3 className="text-3xl font-semibold font-Agatho_Regular text-indigo-600 mb-2">
            Upcoming Events
          </h3>
          <div className="flex flex-col items-center space-y-4">
            <div className="flex flex-row items-center justify-center space-x-3">
              <FaAward className="text-3xl text-indigo-600" />
              <span className="text-xl font-semibold">
                <b className="font-extrabold">Indo European Business Council</b> Corporate Excellence &amp; Business Awards Gala 2025
              </span>
            </div>
       
          </div>
        </div>
      </section>

      {/* News & Insights Section */}
      <section className="py-16 px-6 md:px-20 bg-gray-100">
        <h2 className="text-5xl font-Agatho_Regular font-bold text-gray-900 mb-12 text-center">
          News & Insights
        </h2>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {newsSections.map((section, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center text-center transition-transform hover:scale-105 duration-300"
            >
              <div className="mb-4">{section.icon}</div>
              <h3 className="text-3xl font-Agatho_Regular font-semibold text-gray-900 mb-2">
                {section.title}
              </h3>
              <p className="text-gray-700 text-sm md:text-base ">{section.description}</p>
            </div>
          ))}
        </div>

        {/* Contribute Your Voice */}
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <h3 className="text-4xl font-Agatho_Regular font-bold text-gray-900 mb-6">
            Contribute Your Voice
          </h3>
          <p className="text-gray-700 mb-6">
            Are you an <strong className="font-extrabold">Indo European Business Council</strong> member with insights or
            innovations worth sharing? Join thought leaders driving Indo
            European cooperation.
          </p>
     
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-10">
          <div className="text-center mb-6">
            <Mail size={50} className="text-indigo-600 mx-auto mb-4" />
            <h3 className="text-4xl font-Agatho_Regular font-bold text-gray-900 mb-2">
              Stay Connected with <b className="font-extrabold">Indo European Business Council</b>
            </h3>
       
            <p className="text-gray-700">
              Receive updates on events, business opportunities, and member
              highlights.
            </p>
          </div>
          <form className="grid gap-4 md:grid-cols-2">
            <input
              type="text"
              placeholder="First Name"
              className="border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <input
              type="text"
              placeholder="Last Name"
              className="border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <input
              type="email"
              placeholder="Email Address"
              className="border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 outline-none md:col-span-2"
            />
            <input
              type="text"
              placeholder="Company / Organisation (optional)"
              className="border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 outline-none md:col-span-2"
            />
            <select className="border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 outline-none md:col-span-2">
              <option>Country (optional)</option>
              <option>India</option>
              <option>UK</option>
              <option>Germany</option>
              <option>France</option>
              <option>Other</option>
            </select>
            <button
              type="submit"
              className="bg-indigo-600 text-white rounded-full p-4 font-semibold hover:bg-indigo-700 transition md:col-span-2"
            >
              Subscribe Now
            </button>
          </form>
          <p className="text-sm md:text-base text-gray-500 text-center mt-4">
            You can unsubscribe anytime. We respect your privacy.
          </p>
        </div>
      </section>
    </div>
  );
};

export default IEBCHighlights;
